import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const BOOKING_URL = "https://alchemy-practice.clientsecure.me/";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success
    const [newsletterEmail, setNewsletterEmail] = useState("");
    const [newsletterStatus, setNewsletterStatus] = useState("idle");

    const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const submitContact = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please complete all fields.");
            return;
        }
        setStatus("loading");
        try {
            await axios.post(`${API}/contact`, form);
            setStatus("success");
            toast.success("Your note has been received. Austine will be in touch soon.");
            setForm({ name: "", email: "", message: "" });
            setTimeout(() => setStatus("idle"), 4000);
        } catch (err) {
            setStatus("idle");
            const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
        }
    };

    const submitNewsletter = async (e) => {
        e.preventDefault();
        if (!newsletterEmail.trim()) {
            toast.error("Please enter your email.");
            return;
        }
        setNewsletterStatus("loading");
        try {
            await axios.post(`${API}/newsletter`, { email: newsletterEmail });
            setNewsletterStatus("success");
            toast.success("You're on the list. Welcome.");
            setNewsletterEmail("");
            setTimeout(() => setNewsletterStatus("idle"), 4000);
        } catch (err) {
            setNewsletterStatus("idle");
            toast.error("Please enter a valid email.");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="py-28 lg:py-40" style={{ background: "#FAFAF8" }}>
            <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
                {/* Telehealth image + Final CTA */}
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center mb-24" data-reveal>
                    <div className="lg:col-span-6 order-2 lg:order-1">
                        <span className="editorial-eyebrow">Two ways to connect</span>
                        <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.0] text-charcoal text-balance">
                            Not sure <em className="italic text-gold">where to start?</em>
                        </h2>
                        <p className="mt-8 text-lg text-charcoal-soft leading-[1.85] text-pretty max-w-xl">
                            If you are curious about counseling services — whether EMDR is right for you,
                            what sessions look like, or simply whether we might be a good fit — the best
                            place to begin is a free consultation. It is a short, pressure-free
                            conversation with no obligation to commit. You can ask every question you
                            have there.
                        </p>
                        <p className="mt-5 text-lg text-charcoal-soft leading-[1.85] text-pretty max-w-xl">
                            The contact form below is for general questions and correspondence —
                            anything that does not require scheduling. Austine reads every message
                            personally and responds within two business days.
                        </p>

                        <a
                            href={BOOKING_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="final-cta-book-button"
                            className="mt-10 editorial-button-primary group"
                        >
                            Schedule a Consultation
                            <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>

                        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] tracking-[0.28em] uppercase text-charcoal-soft/80">
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                Secure Telehealth
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                Wisconsin & Colorado
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-serif text-gold text-base leading-none">✦</span>
                                By Appointment
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-6 order-1 lg:order-2">
                        <div className="relative">
                            <img
                                src="https://images.pexels.com/photos/5793982/pexels-photo-5793982.jpeg?auto=compress&cs=tinysrgb&w=1400"
                                alt="A quiet, sunlit desk — laptop, white blooms, ready for a session"
                                className="w-full h-[420px] lg:h-[560px] object-cover"
                            />
                            <div className="absolute -bottom-5 -left-5 hidden md:block">
                                <span className="font-serif text-gold text-6xl leading-none">✦</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="editorial-divider mb-20"><span className="text-lg">✦</span></div>

                {/* Contact + Newsletter */}
                <div className="grid lg:grid-cols-12 gap-16 lg:gap-24">
                    {/* Contact form */}
                    <div className="lg:col-span-7" data-reveal>
                        <span className="editorial-eyebrow">For general inquiries</span>
                        <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-charcoal">
                            Send a note.
                        </h3>
                        <p className="mt-4 text-charcoal-soft max-w-lg leading-relaxed">
                            Share a little about what is bringing you here, and Austine will reply personally —
                            usually within two business days.
                        </p>

                        <form onSubmit={submitContact} className="mt-12 space-y-10" data-testid="contact-form">
                            <div>
                                <label className="editorial-eyebrow text-charcoal/70 block mb-2">Name</label>
                                <input
                                    type="text"
                                    value={form.name}
                                    onChange={onChange("name")}
                                    className="editorial-input"
                                    placeholder="Your name"
                                    data-testid="contact-name-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="editorial-eyebrow text-charcoal/70 block mb-2">Email</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={onChange("email")}
                                    className="editorial-input"
                                    placeholder="you@example.com"
                                    data-testid="contact-email-input"
                                    required
                                />
                            </div>
                            <div>
                                <label className="editorial-eyebrow text-charcoal/70 block mb-2">Message</label>
                                <textarea
                                    value={form.message}
                                    onChange={onChange("message")}
                                    className="editorial-textarea"
                                    placeholder="Tell me what brings you here — whether that is a general question or something about the work I have not covered on the site."
                                    data-testid="contact-message-input"
                                    required
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={status === "loading"}
                                data-testid="contact-submit-button"
                                className="editorial-button-primary group disabled:opacity-60"
                            >
                                {status === "loading" && <Loader2 size={16} className="animate-spin" strokeWidth={1.25} />}
                                {status === "success" && <Check size={16} strokeWidth={1.5} />}
                                {status === "idle" && "Send Your Note"}
                                {status === "loading" && "Sending"}
                                {status === "success" && "Received"}
                                {status === "idle" && (
                                    <ArrowUpRight size={16} strokeWidth={1.25} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Newsletter / sidebar */}
                    <div className="lg:col-span-5" data-reveal>
                        <div className="lg:sticky lg:top-32 p-10 lg:p-12 border" style={{ background: "#F2EDE6", borderColor: "#E2DDD8" }}>
                            <span className="editorial-eyebrow">The Letter</span>
                            <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-charcoal leading-tight">
                                Slow, considered notes — <em className="italic text-gold">a few times a year.</em>
                            </h3>
                            <p className="mt-5 text-charcoal-soft leading-relaxed">
                                Occasional reflections on healing, the body, and the slow work of becoming
                                more yourself. Quiet. Unhurried. Never sold or shared.
                            </p>

                            <form onSubmit={submitNewsletter} className="mt-10 space-y-6" data-testid="newsletter-form">
                                <input
                                    type="email"
                                    value={newsletterEmail}
                                    onChange={(e) => setNewsletterEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    className="editorial-input"
                                    data-testid="newsletter-email-input"
                                    required
                                />
                                <button
                                    type="submit"
                                    disabled={newsletterStatus === "loading"}
                                    data-testid="newsletter-submit-button"
                                    className="editorial-button-ghost w-full justify-center group disabled:opacity-60"
                                >
                                    {newsletterStatus === "loading" && <Loader2 size={14} className="animate-spin" strokeWidth={1.25} />}
                                    {newsletterStatus === "success" && <Check size={14} strokeWidth={1.5} />}
                                    {newsletterStatus === "idle" && "Subscribe"}
                                    {newsletterStatus === "loading" && "Adding"}
                                    {newsletterStatus === "success" && "Welcome"}
                                </button>
                            </form>

                            <div className="mt-10 pt-8 border-t space-y-3 text-[11px] tracking-[0.32em] uppercase text-charcoal-soft" style={{ borderColor: "#E2DDD8" }}>
                                <div className="flex items-center gap-3">
                                    <span className="font-serif text-gold text-base leading-none">✦</span>
                                    Telehealth, WI & CO
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="font-serif text-gold text-base leading-none">✦</span>
                                    By Appointment
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
